import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { getAuthFileUrl } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Save, Send, MessageSquare, ArrowLeft, Calendar,
  RotateCcw, Trash2, Users, LayoutGrid,
  Paperclip, X, FileText, ImageIcon, Loader2,
} from 'lucide-react'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
type DayKey = typeof DAYS[number]
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const ROLE_DISPLAY: Record<string, string> = {
  super_admin: 'Super Admin', admin: 'Admin', director: 'Director', sales_manager: 'Sales Manager',
  asst_sales_manager: 'Asst Sales Manager', sales_executive: 'Sales Executive',
  chief_engineer: 'Chief Engineer', operation_manager: 'Operation Manager',
  engineer: 'Engineer', technician: 'Technician', finance: 'Finance',
  procurement: 'Procurement', storekeeper: 'Storekeeper', hr: 'HR',
}

const TEAM_VIEW_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'chief_engineer', 'operation_manager', 'finance', 'hr']
const COMMENT_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'chief_engineer', 'finance', 'hr']
const ADMIN_DELETE_ROLES = ['super_admin', 'admin', 'director', 'hr']

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function formatDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatWeekLabel(monday: Date): string {
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${monday.toLocaleDateString('en-US', opts)} – ${sunday.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatWeekColHeader(weekStr: string): string {
  const d = new Date(weekStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatMonthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function getTodayMondayStr(): string {
  return formatDate(getMonday(new Date()))
}

interface Attachment {
  id: number
  report_id: number
  day: string
  file_name: string
  sharepoint_path: string
  mime_type: string
  uploaded_by: number
  uploaded_at: string
}

interface Report {
  id: number
  user_id: number
  user_full_name: string
  user_role: string
  week_start: string
  monday: string
  tuesday: string
  wednesday: string
  thursday: string
  friday: string
  saturday: string
  sunday: string
  status: string
  submitted_at: string | null
  created_at: string
  updated_at: string
  attachments?: Record<string, Attachment[]>
}

interface Comment {
  id: number
  report_id: number
  commenter_id: number
  commenter_name: string
  commenter_role: string
  comment: string
  created_at: string
}

interface AttendanceMember {
  user_id: number
  user_full_name: string
  user_role: string
  statuses: Record<string, 'submitted' | 'draft' | 'missed' | 'pending'>
  report_ids: Record<string, number | null>
}

interface AttendanceData {
  weeks: string[]
  members: AttendanceMember[]
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'submitted') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Submitted</span>
  if (status === 'draft') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Draft</span>
  if (status === 'missed') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Missed</span>
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Pending</span>
}

function GridCell({ status, reportId, onOpen }: { status: string; reportId: number | null; onOpen: (id: number) => void }) {
  const canOpen = (status === 'submitted' || status === 'draft') && reportId !== null
  const base = 'w-full h-8 rounded text-xs font-medium flex items-center justify-center transition-colors'
  if (status === 'submitted') return (
    <button onClick={() => canOpen && onOpen(reportId!)} className={`${base} bg-green-100 text-green-700 hover:bg-green-200`}>✓</button>
  )
  if (status === 'draft') return (
    <button onClick={() => canOpen && onOpen(reportId!)} className={`${base} bg-amber-100 text-amber-700 hover:bg-amber-200`}>Draft</button>
  )
  if (status === 'missed') return (
    <div className={`${base} bg-red-100 text-red-500`}>✗</div>
  )
  return <div className={`${base} bg-slate-100 text-slate-400`}>—</div>
}

// ── Attachment display component ──────────────────────────────────────────────
function AttachmentList({
  attachments,
  reportId,
  canDelete,
  onDelete,
  deletingId,
}: {
  attachments: Attachment[]
  reportId: number
  canDelete: boolean
  onDelete?: (att: Attachment) => void
  deletingId?: number | null
}) {
  if (attachments.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map(att => {
        const isImage = att.mime_type.startsWith('image/')
        const downloadUrl = getAuthFileUrl(`/api/weekly-reports/${reportId}/attachments/${att.id}/download`)
        const isDeleting = deletingId === att.id
        return (
          <div
            key={att.id}
            className="group relative flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
          >
            {isImage ? (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" title={att.file_name}>
                <img
                  src={downloadUrl}
                  alt={att.file_name}
                  className="h-12 w-12 object-cover rounded border border-slate-200 hover:opacity-80 transition-opacity cursor-pointer"
                  onError={e => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </a>
            ) : (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-blue-700 hover:text-blue-900 max-w-[160px]"
                title={att.file_name}
              >
                <FileText size={14} className="shrink-0 text-slate-400" />
                <span className="truncate">{att.file_name}</span>
              </a>
            )}
            {canDelete && onDelete && (
              <button
                onClick={() => onDelete(att)}
                disabled={isDeleting}
                className="ml-1 p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                title="Remove"
              >
                {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Upload button for a single day ────────────────────────────────────────────
function DayUploadButton({
  day,
  reportId,
  isUploading,
  onUpload,
}: {
  day: string
  reportId: number | undefined
  isUploading: boolean
  onUpload: (day: DayKey, file: File) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="mt-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) {
            onUpload(day as DayKey, file)
            e.target.value = ''
          }
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded px-2 py-1 transition-colors disabled:opacity-50"
      >
        {isUploading
          ? <><Loader2 size={12} className="animate-spin" /> Uploading…</>
          : <><Paperclip size={12} /> Attach photo or file</>
        }
      </button>
    </div>
  )
}

export default function WeeklyReport() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const role = user?.role || ''
  const hasTeamView = TEAM_VIEW_ROLES.includes(role)
  const canComment = COMMENT_ROLES.includes(role)
  const canAdminDelete = ADMIN_DELETE_ROLES.includes(role)

  const [tab, setTab] = useState<'thisweek' | 'myreports' | 'team'>('thisweek')
  const [teamSubView, setTeamSubView] = useState<'thisweek' | 'monthly'>('thisweek')
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()))
  const [form, setForm] = useState<Record<string, string>>({})
  const [viewingReport, setViewingReport] = useState<number | null>(null)
  const [newComment, setNewComment] = useState('')
  const [saveMsg, setSaveMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set())
  const [uploadingDays, setUploadingDays] = useState<Set<string>>(new Set())
  const [deletingAttId, setDeletingAttId] = useState<number | null>(null)

  const now = new Date()
  const [attendanceYear, setAttendanceYear] = useState(now.getFullYear())
  const [attendanceMonth, setAttendanceMonth] = useState(now.getMonth() + 1)

  const weekKey = formatDate(currentMonday)
  const todayMondayStr = getTodayMondayStr()

  const toggleDay = (day: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  }

  const prevAttendanceMonth = () => {
    if (attendanceMonth === 1) { setAttendanceYear(y => y - 1); setAttendanceMonth(12) }
    else setAttendanceMonth(m => m - 1)
  }
  const nextAttendanceMonth = () => {
    if (attendanceMonth === 12) { setAttendanceYear(y => y + 1); setAttendanceMonth(1) }
    else setAttendanceMonth(m => m + 1)
  }

  const { data: myWeek, isLoading: loadingWeek } = useQuery<Report | null>({
    queryKey: ['weekly-report-my', weekKey],
    queryFn: () => api.get(`/weekly-reports/my/${weekKey}`).then(r => r.data),
    enabled: tab === 'thisweek' && viewingReport === null,
  })

  const { data: myReports } = useQuery<Report[]>({
    queryKey: ['weekly-report-my-list'],
    queryFn: () => api.get('/weekly-reports/my').then(r => r.data),
    enabled: tab === 'myreports',
  })

  const submittedMyReports = (myReports || []).filter(r => r.status === 'submitted')

  const { data: teamReports, error: teamReportsError } = useQuery<Report[]>({
    queryKey: ['weekly-report-team', user?.id],
    queryFn: () => api.get('/weekly-reports/all').then(r => r.data),
    enabled: tab === 'team' && hasTeamView,
  })

  const { data: attendance, isLoading: loadingAttendance, error: attendanceError } = useQuery<AttendanceData>({
    queryKey: ['weekly-report-attendance', attendanceYear, attendanceMonth, user?.id],
    queryFn: () => api.get(`/weekly-reports/team-attendance?year=${attendanceYear}&month=${attendanceMonth}`).then(r => r.data),
    enabled: tab === 'team' && hasTeamView,
  })

  const currentYear = now.getFullYear()
  const currentMonthNum = now.getMonth() + 1
  const { data: currentAttendance, error: currentAttendanceError } = useQuery<AttendanceData>({
    queryKey: ['weekly-report-attendance', currentYear, currentMonthNum, user?.id],
    queryFn: () => api.get(`/weekly-reports/team-attendance?year=${currentYear}&month=${currentMonthNum}`).then(r => r.data),
    enabled: tab === 'team' && hasTeamView && teamSubView === 'thisweek',
  })

  const { data: viewReport } = useQuery<Report | null>({
    queryKey: ['weekly-report-view', viewingReport],
    queryFn: () => viewingReport ? api.get(`/weekly-reports/${viewingReport}`).then(r => r.data) : null,
    enabled: viewingReport !== null,
  })

  const { data: comments } = useQuery<Comment[]>({
    queryKey: ['weekly-report-comments', viewingReport],
    queryFn: () => viewingReport ? api.get(`/weekly-reports/${viewingReport}/comments`).then(r => r.data) : [],
    enabled: viewingReport !== null,
  })

  // Sync form from loaded week
  const prevWeekKeyRef = { current: '' }
  if (myWeek && tab === 'thisweek' && viewingReport === null && weekKey !== prevWeekKeyRef.current) {
    prevWeekKeyRef.current = weekKey
  }

  // Update form when myWeek loads or week changes
  const [lastSyncedWeek, setLastSyncedWeek] = useState('')
  if (myWeek && weekKey !== lastSyncedWeek && tab === 'thisweek' && viewingReport === null) {
    setLastSyncedWeek(weekKey)
    setForm({
      monday: myWeek.monday || '',
      tuesday: myWeek.tuesday || '',
      wednesday: myWeek.wednesday || '',
      thursday: myWeek.thursday || '',
      friday: myWeek.friday || '',
      saturday: myWeek.saturday || '',
      sunday: myWeek.sunday || '',
    })
  } else if (!myWeek && weekKey !== lastSyncedWeek && !loadingWeek && tab === 'thisweek' && viewingReport === null) {
    setLastSyncedWeek(weekKey)
    setForm({ monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '', sunday: '' })
  }

  const saveDraft = useMutation({
    mutationFn: () => api.post('/weekly-reports', { week_start: weekKey, ...form }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my-list'] })
      setSaveMsg('Draft saved')
      setTimeout(() => setSaveMsg(''), 3000)
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => { setErrorMsg(err.response?.data?.detail || 'Failed to save'); setTimeout(() => setErrorMsg(''), 5000) },
  })

  const submitReport = useMutation({
    mutationFn: async () => {
      const saveRes = await api.post('/weekly-reports', { week_start: weekKey, ...form })
      const reportId = saveRes.data.id
      return api.post(`/weekly-reports/${reportId}/submit`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my-list'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-team'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-attendance'] })
      setSaveMsg('Report submitted!')
      setTimeout(() => setSaveMsg(''), 3000)
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => { setErrorMsg(err.response?.data?.detail || 'Failed to submit'); setTimeout(() => setErrorMsg(''), 5000) },
  })

  const addComment = useMutation({
    mutationFn: () => api.post(`/weekly-reports/${viewingReport}/comments`, { comment: newComment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-report-comments', viewingReport] })
      setNewComment('')
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => { setErrorMsg(err.response?.data?.detail || 'Failed to add comment'); setTimeout(() => setErrorMsg(''), 5000) },
  })

  const recallReport = useMutation({
    mutationFn: (reportId: number) => api.post(`/weekly-reports/${reportId}/recall`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my-list'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-team'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-attendance'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-view', viewingReport] })
      setSaveMsg('Report withdrawn — you can now edit and resubmit.')
      setTimeout(() => setSaveMsg(''), 5000)
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => { setErrorMsg(err.response?.data?.detail || 'Failed to withdraw report'); setTimeout(() => setErrorMsg(''), 5000) },
  })

  const deleteReport = useMutation({
    mutationFn: (reportId: number) => api.delete(`/weekly-reports/${reportId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-report-team'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my-list'] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-attendance'] })
      setViewingReport(null)
      setSaveMsg('Report deleted.')
      setTimeout(() => setSaveMsg(''), 4000)
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => { setErrorMsg(err.response?.data?.detail || 'Failed to delete report'); setTimeout(() => setErrorMsg(''), 5000) },
  })

  // ── Attachment upload ─────────────────────────────────────────────────────
  const handleUpload = async (day: DayKey, file: File) => {
    setUploadingDays(prev => new Set(prev).add(day))
    setErrorMsg('')
    try {
      // Ensure a draft exists so we have a report ID
      let reportId = myWeek?.id
      if (!reportId) {
        const res = await api.post('/weekly-reports', { week_start: weekKey, ...form })
        reportId = res.data.id
        queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
      }
      const fd = new FormData()
      fd.append('day', day)
      fd.append('file', file)
      await api.post(`/weekly-reports/${reportId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
    } catch (err: any) {
      setErrorMsg(err.response?.data?.detail || 'Upload failed')
      setTimeout(() => setErrorMsg(''), 5000)
    } finally {
      setUploadingDays(prev => { const s = new Set(prev); s.delete(day); return s })
    }
  }

  // ── Attachment delete ─────────────────────────────────────────────────────
  const handleDeleteAttachment = async (reportId: number, att: Attachment) => {
    if (!confirm(`Remove "${att.file_name}"?`)) return
    setDeletingAttId(att.id)
    try {
      await api.delete(`/weekly-reports/${reportId}/attachments/${att.id}`)
      queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
      queryClient.invalidateQueries({ queryKey: ['weekly-report-view', reportId] })
    } catch (err: any) {
      setErrorMsg(err.response?.data?.detail || 'Failed to remove attachment')
      setTimeout(() => setErrorMsg(''), 5000)
    } finally {
      setDeletingAttId(null)
    }
  }

  const prevWeek = () => {
    const d = new Date(currentMonday)
    d.setDate(d.getDate() - 7)
    setCurrentMonday(d)
  }
  const nextWeek = () => {
    const d = new Date(currentMonday)
    d.setDate(d.getDate() + 7)
    setCurrentMonday(d)
  }

  const isSubmitted = myWeek?.status === 'submitted'

  // ── Report detail view ────────────────────────────────────────────────────
  if (viewingReport !== null && viewReport) {
    const isOwnReport = viewReport.user_id === user?.id
    const isDraft = viewReport.status === 'draft'
    return (
      <div className="space-y-4 max-w-3xl mx-auto">
        <button onClick={() => setViewingReport(null)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{viewReport.user_full_name}</h2>
              <p className="text-sm text-slate-500">{ROLE_DISPLAY[viewReport.user_role] || viewReport.user_role}</p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-slate-700">Week of {formatWeekLabel(new Date(viewReport.week_start + 'T00:00:00'))}</div>
              {viewReport.submitted_at && (
                <div className="text-xs text-green-600 mt-1">Submitted {formatDateShort(viewReport.submitted_at)}</div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {DAYS.map((day, i) => {
              const text = viewReport[day as DayKey] || ''
              const dayAttachments = (viewReport.attachments || {})[day] || []
              const isCollapsed = collapsedDays.has(day)
              const hasContent = text.trim().length > 0 || dayAttachments.length > 0
              const canDeleteAtt = isOwnReport && isDraft
              return (
                <div key={day} className="border border-slate-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleDay(day)}
                    className="w-full px-3 py-2 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-xs font-semibold text-slate-600 uppercase">{DAY_LABELS[i]}</span>
                    <div className="flex items-center gap-2">
                      {dayAttachments.length > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-slate-400">
                          <Paperclip size={11} />{dayAttachments.length}
                        </span>
                      )}
                      {!hasContent && <span className="text-xs text-slate-300 italic">No entry</span>}
                      {isCollapsed ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronUp size={14} className="text-slate-400" />}
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className="px-3 py-2">
                      {text ? (
                        <div className="text-sm text-slate-800 whitespace-pre-wrap">{text}</div>
                      ) : (
                        <p className="text-sm text-slate-300 italic">No entry</p>
                      )}
                      <AttachmentList
                        attachments={dayAttachments}
                        reportId={viewReport.id}
                        canDelete={canDeleteAtt}
                        onDelete={att => handleDeleteAttachment(viewReport.id, att)}
                        deletingId={deletingAttId}
                      />
                      {/* Owner can add attachments to their own draft from this view */}
                      {canDeleteAtt && (
                        <DayUploadButton
                          day={day}
                          reportId={viewReport.id}
                          isUploading={uploadingDays.has(day)}
                          onUpload={async (d, file) => {
                            setUploadingDays(prev => new Set(prev).add(d))
                            try {
                              const fd = new FormData()
                              fd.append('day', d)
                              fd.append('file', file)
                              await api.post(`/weekly-reports/${viewReport.id}/attachments`, fd, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                              })
                              queryClient.invalidateQueries({ queryKey: ['weekly-report-view', viewReport.id] })
                              queryClient.invalidateQueries({ queryKey: ['weekly-report-my', weekKey] })
                            } catch (e: any) {
                              setErrorMsg(e.response?.data?.detail || 'Upload failed')
                            } finally {
                              setUploadingDays(prev => { const s = new Set(prev); s.delete(d); return s })
                            }
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {canAdminDelete && viewReport.user_id !== user?.id && (
            <div className="mt-3 flex gap-2">
              {viewReport.status === 'submitted' && (
                <button
                  onClick={() => {
                    if (confirm(`Withdraw ${viewReport.user_full_name}'s report? It will go back to draft so they can edit and resubmit.`)) {
                      recallReport.mutate(viewReport.id)
                    }
                  }}
                  disabled={recallReport.isPending}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  <RotateCcw size={14} /> {recallReport.isPending ? 'Withdrawing...' : 'Withdraw'}
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm(`Permanently delete ${viewReport.user_full_name}'s report for week of ${viewReport.week_start}? This cannot be undone.`)) {
                    deleteReport.mutate(viewReport.id)
                  }
                }}
                disabled={deleteReport.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 size={14} /> {deleteReport.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
            <MessageSquare size={16} /> Comments ({comments?.length || 0})
          </h3>

          {(comments || []).length === 0 && (
            <p className="text-sm text-slate-400 italic mb-3">No comments yet</p>
          )}

          <div className="space-y-3 mb-4">
            {(comments || []).map(c => (
              <div key={c.id} className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-900">{c.commenter_name}</span>
                  <span className="text-xs text-slate-400">{ROLE_DISPLAY[c.commenter_role] || c.commenter_role}</span>
                  <span className="text-xs text-slate-400 ml-auto">{formatDateShort(c.created_at)}</span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.comment}</p>
              </div>
            ))}
          </div>

          {canComment && viewReport.user_id !== user?.id && (
            <div className="flex gap-2">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-700"
                rows={2}
              />
              <button
                onClick={() => { if (newComment.trim()) addComment.mutate() }}
                disabled={!newComment.trim() || addComment.isPending}
                className="px-4 py-2 bg-red-800 text-white text-sm rounded-lg hover:bg-red-900 disabled:opacity-50 self-end"
              >
                Send
              </button>
            </div>
          )}
        </div>

        {errorMsg && <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">{errorMsg}</div>}
      </div>
    )
  }

  // ── Main view ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Weekly Report</h1>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        <button
          onClick={() => { setTab('thisweek'); setViewingReport(null) }}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'thisweek' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          This Week
        </button>
        <button
          onClick={() => { setTab('myreports'); setViewingReport(null) }}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'myreports' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          My Reports
        </button>
        {hasTeamView && (
          <button
            onClick={() => { setTab('team'); setViewingReport(null) }}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'team' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            My Team
          </button>
        )}
      </div>

      {saveMsg && <div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-lg border border-green-200">{saveMsg}</div>}
      {errorMsg && <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">{errorMsg}</div>}

      {/* ── This Week tab ── */}
      {tab === 'thisweek' && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <button onClick={prevWeek} className="p-2 hover:bg-slate-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center">
                <ChevronLeft size={20} />
              </button>
              <div className="text-center">
                <div className="text-sm font-semibold text-slate-900">{formatWeekLabel(currentMonday)}</div>
                {isSubmitted && <span className="text-xs text-green-600 font-medium">Submitted</span>}
                {!isSubmitted && myWeek && <span className="text-xs text-amber-600 font-medium">Draft</span>}
              </div>
              <button onClick={nextWeek} className="p-2 hover:bg-slate-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {loadingWeek ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800"></div></div>
          ) : (
            <div className="space-y-3">
              {DAYS.map((day, i) => {
                const dayDate = new Date(currentMonday)
                dayDate.setDate(currentMonday.getDate() + i)
                const dayStr = dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                const isCollapsed = collapsedDays.has(day)
                const dayAttachments = (myWeek?.attachments || {})[day] || []
                const canDeleteAtt = !isSubmitted && !!myWeek?.id
                return (
                  <div key={day} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <button
                      onClick={() => toggleDay(day)}
                      className={`w-full px-4 py-2.5 flex items-center justify-between ${i >= 5 ? 'bg-slate-50' : 'bg-white'} border-b border-slate-100 hover:bg-slate-50 transition-colors`}
                    >
                      <span className="text-sm font-semibold text-slate-800">{DAY_LABELS[i]}</span>
                      <div className="flex items-center gap-2">
                        {dayAttachments.length > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-slate-400">
                            <Paperclip size={11} />{dayAttachments.length}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">{dayStr}</span>
                        {isCollapsed ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronUp size={14} className="text-slate-400" />}
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="p-3">
                        {isSubmitted ? (
                          <div className="text-sm text-slate-700 whitespace-pre-wrap min-h-[40px]">
                            {form[day] || <span className="text-slate-300 italic">No report</span>}
                          </div>
                        ) : (
                          <textarea
                            value={form[day] || ''}
                            onChange={e => setForm({ ...form, [day]: e.target.value })}
                            placeholder={i >= 5 ? 'Optional — leave blank if not working' : 'What did you do today?'}
                            className="w-full text-sm border-0 focus:outline-none resize-none min-h-[80px] placeholder:text-slate-300"
                            rows={3}
                          />
                        )}
                        {/* Attachments */}
                        <AttachmentList
                          attachments={dayAttachments}
                          reportId={myWeek?.id || 0}
                          canDelete={canDeleteAtt}
                          onDelete={att => myWeek?.id && handleDeleteAttachment(myWeek.id, att)}
                          deletingId={deletingAttId}
                        />
                        {/* Upload button (only in draft mode) */}
                        {!isSubmitted && (
                          <DayUploadButton
                            day={day}
                            reportId={myWeek?.id}
                            isUploading={uploadingDays.has(day)}
                            onUpload={handleUpload}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              {isSubmitted && myWeek && (
                <div className="pb-4">
                  <button
                    onClick={() => {
                      if (confirm('Withdraw this report? It will go back to draft and you can edit and resubmit it.')) {
                        recallReport.mutate(myWeek.id)
                      }
                    }}
                    disabled={recallReport.isPending}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    <RotateCcw size={16} /> {recallReport.isPending ? 'Withdrawing...' : 'Withdraw Report'}
                  </button>
                </div>
              )}

              {!isSubmitted && (
                <div className="flex gap-3 pb-4">
                  <button
                    onClick={() => saveDraft.mutate()}
                    disabled={saveDraft.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Save size={16} /> {saveDraft.isPending ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Submit this weekly report? You will not be able to edit it after submitting.')) {
                        submitReport.mutate()
                      }
                    }}
                    disabled={submitReport.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-800 text-white rounded-xl text-sm font-medium hover:bg-red-900 disabled:opacity-50"
                  >
                    <Send size={16} /> {submitReport.isPending ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── My Reports tab ── */}
      {tab === 'myreports' && (
        <div className="space-y-2">
          {submittedMyReports.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No submitted reports yet</p>
            </div>
          )}

          {submittedMyReports.map(r => (
            <button
              key={r.id}
              onClick={() => setViewingReport(r.id)}
              className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-4 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-900">
                  Week of {formatWeekLabel(new Date(r.week_start + 'T00:00:00'))}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                  Submitted
                </span>
              </div>
              {r.submitted_at && (
                <div className="text-xs text-slate-400 mt-1">Submitted {formatDateShort(r.submitted_at)}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── My Team tab ── */}
      {tab === 'team' && hasTeamView && (
        <>
          {(teamReportsError || (teamSubView === 'thisweek' ? currentAttendanceError : attendanceError)) && (
            <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {(teamReportsError as any)?.response?.data?.detail
                || ((teamSubView === 'thisweek' ? currentAttendanceError : attendanceError) as any)?.response?.data?.detail
                || 'Unable to load team reports. Please try again or ask an administrator to check reporting assignments.'}
            </div>
          )}
        <div className="space-y-4">

          {/* Sub-view toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setTeamSubView('thisweek')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${teamSubView === 'thisweek' ? 'bg-red-800 text-white border-red-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              <Users size={14} /> This Week
            </button>
            <button
              onClick={() => setTeamSubView('monthly')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${teamSubView === 'monthly' ? 'bg-red-800 text-white border-red-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              <LayoutGrid size={14} /> Monthly Grid
            </button>
          </div>

          {/* ── This Week sub-view ── */}
          {teamSubView === 'thisweek' && (
            <div className="space-y-3">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Week of {formatWeekLabel(new Date(todayMondayStr + 'T00:00:00'))}
                  </h3>
                </div>

                {loadingAttendance ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-800"></div>
                  </div>
                ) : !currentAttendance || currentAttendance.members.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Users size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">No team members found</p>
                  </div>
                ) : (() => {
                  const members = currentAttendance.members
                  const sorted = [...members].sort((a, b) => {
                    const order = { missed: 0, draft: 1, pending: 2, submitted: 3 }
                    const sa = order[a.statuses[todayMondayStr] as keyof typeof order] ?? 2
                    const sb = order[b.statuses[todayMondayStr] as keyof typeof order] ?? 2
                    return sa - sb || a.user_full_name.localeCompare(b.user_full_name)
                  })
                  const missedCount = sorted.filter(m => m.statuses[todayMondayStr] === 'missed').length
                  const submittedCount = sorted.filter(m => m.statuses[todayMondayStr] === 'submitted').length

                  return (
                    <>
                      {missedCount > 0 && (
                        <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs font-medium text-red-700">
                          {missedCount} member{missedCount > 1 ? 's' : ''} have not submitted yet
                        </div>
                      )}
                      {missedCount === 0 && submittedCount === sorted.length && (
                        <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-xs font-medium text-green-700">
                          All {submittedCount} members have submitted ✓
                        </div>
                      )}
                      <div className="divide-y divide-slate-100">
                        {sorted.map(m => {
                          const status = m.statuses[todayMondayStr] || 'pending'
                          const reportId = m.report_ids[todayMondayStr]
                          const canClick = (status === 'submitted' || status === 'draft') && reportId !== null
                          return (
                            <div
                              key={m.user_id}
                              onClick={() => canClick && setViewingReport(reportId!)}
                              className={`flex items-center justify-between px-4 py-3 ${canClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                            >
                              <div>
                                <div className="text-sm font-medium text-slate-900">{m.user_full_name}</div>
                                <div className="text-xs text-slate-400">{ROLE_DISPLAY[m.user_role] || m.user_role}</div>
                              </div>
                              <StatusBadge status={status} />
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )
                })()}
              </div>

              {/* Recent submitted reports */}
              {(teamReports || []).length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1 mb-2">Recent Submitted Reports</h3>
                  <div className="space-y-2">
                    {(teamReports || []).slice(0, 10).map(r => (
                      <button
                        key={r.id}
                        onClick={() => setViewingReport(r.id)}
                        className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-slate-900">{r.user_full_name}</div>
                            <div className="text-xs text-slate-400">{ROLE_DISPLAY[r.user_role] || r.user_role}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-medium text-slate-600">
                              {formatWeekLabel(new Date(r.week_start + 'T00:00:00'))}
                            </div>
                            {r.submitted_at && (
                              <div className="text-xs text-green-600 mt-0.5">Submitted {formatDateShort(r.submitted_at)}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(teamReports || []).length === 0 && !loadingAttendance && (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No team reports submitted yet</p>
                </div>
              )}
            </div>
          )}

          {/* ── Monthly Grid sub-view ── */}
          {teamSubView === 'monthly' && (
            <div className="space-y-3">
              {/* Month navigator */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex items-center justify-between">
                <button onClick={prevAttendanceMonth} className="p-2 hover:bg-slate-100 rounded-lg min-w-[40px] min-h-[40px] flex items-center justify-center">
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm font-semibold text-slate-900">{formatMonthLabel(attendanceYear, attendanceMonth)}</span>
                <button onClick={nextAttendanceMonth} className="p-2 hover:bg-slate-100 rounded-lg min-w-[40px] min-h-[40px] flex items-center justify-center">
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 px-1">
                {[
                  { label: 'Submitted', cls: 'bg-green-100 text-green-700' },
                  { label: 'Draft', cls: 'bg-amber-100 text-amber-700' },
                  { label: 'Missed', cls: 'bg-red-100 text-red-500' },
                  { label: 'Not due yet', cls: 'bg-slate-100 text-slate-400' },
                ].map(l => (
                  <span key={l.label} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${l.cls}`}>{l.label}</span>
                ))}
              </div>

              {/* Grid */}
              {loadingAttendance ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800"></div>
                </div>
              ) : !attendance || attendance.members.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <Users size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No team members found</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-2.5 text-xs font-semibold text-slate-600 whitespace-nowrap min-w-[140px]">Member</th>
                          {attendance.weeks.map(ws => (
                            <th key={ws} className="px-2 py-2.5 text-center text-xs font-semibold text-slate-600 whitespace-nowrap min-w-[72px]">
                              {formatWeekColHeader(ws)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {attendance.members.map(m => (
                          <tr key={m.user_id} className="hover:bg-slate-50 transition-colors">
                            <td className="sticky left-0 z-10 bg-white hover:bg-slate-50 px-4 py-2.5 whitespace-nowrap">
                              <div className="text-xs font-medium text-slate-900">{m.user_full_name}</div>
                              <div className="text-xs text-slate-400">{ROLE_DISPLAY[m.user_role] || m.user_role}</div>
                            </td>
                            {attendance.weeks.map(ws => (
                              <td key={ws} className="px-2 py-2">
                                <GridCell
                                  status={m.statuses[ws] || 'pending'}
                                  reportId={m.report_ids[ws]}
                                  onOpen={(id) => setViewingReport(id)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary row */}
                  <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                    {attendance.weeks.map(ws => {
                      const members = attendance.members
                      const submitted = members.filter(m => m.statuses[ws] === 'submitted').length
                      const missed = members.filter(m => m.statuses[ws] === 'missed').length
                      const total = members.length
                      return (
                        <span key={ws} className="whitespace-nowrap">
                          <span className="font-medium text-slate-700">{formatWeekColHeader(ws)}:</span>{' '}
                          <span className="text-green-600">{submitted} submitted</span>
                          {missed > 0 && <span className="text-red-500">, {missed} missed</span>}
                          <span className="text-slate-400"> / {total}</span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}
    </div>
  )
}
